import type { VisitInspectionAnswerDisplay } from "@/Domain/VisitInspectionAnswerDisplay";
import type { VisitQuestionRuleDisplay } from "@/Domain/VisitQuestionRuleDisplay";
import { EnumVisitQuestionRuleType } from "@/Domain/EnumVisitQuestionRuleType";
import { EnumVisitQuestionType } from "@/Domain/EnumVisitQuestionType";
import { useInspectionStore } from "@/stores/useInspectionStore";

/**
 * Composable for evaluating question visibility based on dependency rules
 */
export function useQuestionVisibility() {
  const inspectionStore = useInspectionStore();

  /**
   * Evaluates whether a question should be visible based on its dependency rules.
   * Implements AND logic across different questions (DependsOnQuestionId) and OR logic within the same question.
   *
   * @param answer - The question answer to evaluate
   * @returns true if the question should be visible, false otherwise
   */
  const isQuestionVisible = (answer: VisitInspectionAnswerDisplay, visiting: Set<number> = new Set()): boolean => {
    // Cycle detection: if we're already evaluating this question's visibility on the current
    // path, a circular dependency exists. Treat the question as not visible to break the cycle.
    if (visiting.has(answer.QuestionId)) return false;

    // If no rules, question is always visible
    if (!answer.Rules || answer.Rules.length === 0) return true;

    const dependencyRules = answer.Rules;

    // If no dependency rules, question is visible
    if (dependencyRules.length === 0) return true;

    // Get context from store
    const allAnswers = inspectionStore.formData.answers || [];
    const jewishOwned = inspectionStore.formData.jewishOwned ?? inspectionStore.companyJewishOwned ?? null;

    // Group rules by DependsOnQuestionId (treating JewishOwned rules as a separate group)
    const ruleGroups = new Map<string | number | null, VisitQuestionRuleDisplay[]>();

    dependencyRules.forEach((rule) => {
      const key = rule.RuleType === EnumVisitQuestionRuleType.JewishOwned ? "JewishOwned" : rule.DependsOnQuestionId;

      if (!ruleGroups.has(key)) ruleGroups.set(key, []);
      ruleGroups.get(key)!.push(rule);
    });

    // Mark this question as being evaluated on the current path.
    // Use try/finally so the mark is always removed on the way back up (backtracking),
    // which allows diamond-shaped dependencies (A→B, A→C, B→D, C→D) to work correctly.
    visiting.add(answer.QuestionId);
    try {
      // OR across different question groups
      // For each group, OR the rules within it
      return Array.from(ruleGroups.values()).some((groupRules) => {
        // At least one rule in this group must pass (OR logic)
        return groupRules.some((rule) => evaluateRule(rule, allAnswers, jewishOwned, visiting));
      });
    } finally {
      visiting.delete(answer.QuestionId);
    }
  };

  const evaluateRule = (
    rule: VisitQuestionRuleDisplay,
    allAnswers: VisitInspectionAnswerDisplay[],
    jewishOwned: boolean | null,
    visiting: Set<number>
  ): boolean => {
    let rulePasses = false;

    if (rule.RuleType === EnumVisitQuestionRuleType.AnswerEquals)
      rulePasses = evaluateAnswerEqualsRule(rule, allAnswers, visiting);
    else if (rule.RuleType === EnumVisitQuestionRuleType.JewishOwned)
      rulePasses = evaluateJewishOwnedRule(rule, jewishOwned);

    // Handle negative dependencies: hide if rule passes, show if rule fails
    return rule.IsNegativeDependency ? !rulePasses : rulePasses;
  };

  /**
   * Evaluates an AnswerEquals rule
   * Checks current answer first, then falls back to previous response if no current answer
   */
  const evaluateAnswerEqualsRule = (
    rule: VisitQuestionRuleDisplay,
    allAnswers: VisitInspectionAnswerDisplay[],
    visiting: Set<number>
  ): boolean => {
    if (!rule.DependsOnQuestionId || !rule.DependsOnAnswerValue) {
      return false;
    }

    const parentAnswer = allAnswers.find((a) => a.QuestionId === rule.DependsOnQuestionId);

    if (!parentAnswer) {
      return false;
    }

    const isCheckbox = parentAnswer.VisitQuestionType === EnumVisitQuestionType.Checkboxes;

    // For checkboxes, AnswerValue is semicolon-delimited (e.g. "Option A;Option B").
    // Check for inclusion; for all other types use exact equality.
    const matchesValue = (answerValue: string | null | undefined): boolean => {
      if (!answerValue) return false;
      if (isCheckbox) return answerValue.split(";").includes(rule.DependsOnAnswerValue!);
      return answerValue === rule.DependsOnAnswerValue;
    };

    // Only consider the current answer if the parent question is itself visible.
    const parentVisible = isQuestionVisible(parentAnswer, visiting);
    if (parentVisible && parentAnswer.AnswerValue) {
      return matchesValue(parentAnswer.AnswerValue);
    }

    // Fall back to previous response when parent is hidden or has no current answer.
    if (parentAnswer.PreviousResponses && parentAnswer.PreviousResponses.length > 0) {
      // Get the latest previous response by ValidFromTime
      const latestPreviousResponse = parentAnswer.PreviousResponses.reduce((latest, current) => {
        return new Date(current.ValidFromTime) > new Date(latest.ValidFromTime) ? current : latest;
      });
      return matchesValue(latestPreviousResponse?.AnswerValue);
    }

    return false;
  };

  /**
   * Evaluates a JewishOwned static field rule
   */
  const evaluateJewishOwnedRule = (rule: VisitQuestionRuleDisplay, jewishOwned: boolean | null): boolean => {
    const expectedValue = rule.DependsOnAnswerValue || rule.RuleText;

    if (jewishOwned === null || !expectedValue) {
      return false;
    }

    const jewishOwnedString = jewishOwned ? "Yes" : "No";
    const expectedLower = expectedValue.toLowerCase();

    return (
      jewishOwnedString.toLowerCase() === expectedLower ||
      (expectedLower === "true" && jewishOwned) ||
      (expectedLower === "false" && !jewishOwned)
    );
  };

  return {
    isQuestionVisible,
  };
}
